<template>
  <UIModal
    class="ModalAnnouncement"
    size="sm"
    :isOpen="!!active"
    :ariaLabel="active?.ariaLabel"
    :headerImageSrc="active?.imageSrc"
    :headerImageAlt="active?.imageAlt"
    @close="close"
  >
    <template #content>
      <h2 v-if="active?.title" class="ModalAnnouncement__title">
        {{ active.title }}
      </h2>

      <!-- Admin-authored or static config, sanitised in `announcementModalView`
           and never user input. -->
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div class="ModalAnnouncement__body" v-html="active?.bodyHtml" />
    </template>

    <!-- Owning the container is what lets the footer push the reminder
         checkbox and the button apart; the element is still ui-lib's own. -->
    <template #actions-container>
      <UIModalActions v-if="active" class="ModalAnnouncement__actions">
        <UIInputCheckboxBase
          v-if="active.reminderLabel"
          name="announcementReminder"
          :label="active.reminderLabel"
          :modelValue="isReminderChecked"
          @update:modelValue="(value: boolean) => (isReminderChecked = value)"
        />
        <span v-else class="ModalAnnouncement__spacer" />

        <UIButton theme="primary" :trailIcon="active.buttonIcon" @click="close">
          {{ active.buttonLabel }}
        </UIButton>
      </UIModalActions>
    </template>
  </UIModal>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'

import {
  UIButton,
  UIInputCheckboxBase,
  UIModal,
  UIModalActions,
} from '@foundever/ui-lib'

import { useAnnouncementModals } from './useAnnouncementModals'

const { active, handleClose } = useAnnouncementModals()

const isReminderChecked = ref(false)

// Reset the checkbox whenever a different modal becomes active so its state
// never leaks between the queued modals.
watch(
  () => active.value?.id,
  () => {
    isReminderChecked.value = false
  },
)

/**
 * The only way out. Passing `headerImageSrc` makes ui-lib treat the dialog as
 * acknowledge-only — no close control, and Escape and backdrop clicks do not
 * dismiss it — so every close records the dismissal.
 */
const close = () => {
  handleClose(isReminderChecked.value)
}
</script>

<style lang="scss">
/* Unscoped: the dialog teleports to `#AppContainer`, so `data-v` scoping would
   never reach the markup. The preview panel in Admin → Announcements mirrors
   these classes so both surfaces are styled from one place. */
.ModalAnnouncement__title {
  @include font-heading3;
  margin-bottom: var(---fds-global-space-xs);
  color: var(--text-primary, #12123f);
}

/* The body is `v-html` rich text, so its children are whatever the editor
   serialised. `serializeRichText` only ever emits `<p>` / `<h3>` blocks — never a
   bare text node or a top-level `<br>` — so a flex column spaces every block
   uniformly and keeps working if a new block type is added. */
.ModalAnnouncement__body {
  @include font-body-small-regular;
  display: flex;
  flex-direction: column;
  gap: var(---fds-global-space-xs);
  color: var(--text-secondary, #57576b);

  h3 {
    @include font-heading3;
    color: var(--text-primary, #12123f);
  }

  p {
    color: var(--text-secondary, #57576b);
  }

  a {
    color: var(--text-accent, #5959eb);
    text-decoration: underline;
  }
}

/* Pushes the reminder checkbox and the primary button apart. Three classes deep
   to outrank ui-lib's own scoped `justify-content`. */
.UIModalContainer .ModalAnnouncement__actions.UIModalActions {
  justify-content: space-between;
}
</style>
